import React, { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useProductFormAPI } from './hooks/useProductFormAPI';
import { initializeForm, resetForm, updateFormField } from '../../../../../store/actions/productFormActions';
import { RootState } from '../../../../../store/types';
import { useProductFormStyles } from './ProductForm.style';
import { ProductForm } from './ProductForm';
import { ProductPublicEditBanner } from './ProductPublicEditBanner';
import { Box, Button, CircularProgress, Drawer, IconButton, Typography } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { FEATURE_FLAGS } from './constants';
import CloseIcon from '@material-ui/icons/Close';

export interface ProductEditorLogic {
  handleSubmit: (event: React.FormEvent) => Promise<void>;
  onMakePublicConfirm: () => Promise<void>;
  isLoading: boolean;
  hasError: boolean;
  error: any;
  submitButtonText: string;
  hasChanges: boolean;
}

export interface ProductEditorProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (product: any, shouldNavigate?: boolean) => void;
  product: any;
}

export const useProductEditor = ({ open, onClose, onSubmit, product }: ProductEditorProps): ProductEditorLogic => {
  const dispatch = useDispatch();
  
  // Get form state from Redux
  const formData = useSelector((state: RootState) => state.productForm);
  const { isFormValid } = formData;
  
  // API hooks
  const { 
    editProductState, 
    updateExistingProduct,
    isLoading,
    hasError,
    error,
    resetError
  } = useProductFormAPI();

  // Initialize form when component mounts or product changes
  useEffect(() => {
    if (open && product) {
      dispatch(initializeForm(product, true));
    }
  }, [open, product, dispatch]);
  
  // Reset form and error when closed
  useEffect(() => {
    if (!open) {
      dispatch(resetForm());
      resetError();
    }
  }, [open, dispatch, resetError]);

  // Check if form data has changed from the original product data
  const hasChanges = useMemo(() => {
    if (!product) return false;
    
    // Compare description
    const originalDescription = product.longDescription || '';
    const currentDescription = formData.description.value;
    if (originalDescription !== currentDescription) return true;

    // Compare scope — only when flag is enabled and product has no existing scope (once set it's immutable)
    if (FEATURE_FLAGS.PRODUCT_SCOPE && !product.scope) {
      const currentScope = formData.scope?.value?.trim() || '';
      if (currentScope) return true;
    }
    
    // Compare visibility
    const originalVisibility = product.visibility || 'team';
    if (originalVisibility !== formData.visibility.value) return true;
    
    // Compare approval flow (map API value to UI value)
    const originalApprovalType = product.publishedInfo?.[0]?.approvalType;
    const originalApprovalFlow = originalApprovalType === 'MANUAL' ? 'closed' : 'open';
    if (originalApprovalFlow !== formData.approvalFlow.value) return true;
    
    // Compare application types and redirect URLs (dynamic)
    if (FEATURE_FLAGS.PRODUCT_METADATA_CONFIG) {
      const originalTypes = product.applicationTypes || [];
      const originalCodes = (originalTypes as { type?: string }[]).map((t: any) => t.type).filter(Boolean);
      const currentCodes = formData.selectedApplicationTypeCodes.value;
      if (originalCodes.length !== currentCodes.length || originalCodes.some((c, i) => c !== currentCodes[i])) return true;
      const originalRedirectByType: Record<string, string[]> = {};
      (originalTypes as { type?: string; redirectUrls?: string[] }[]).forEach((t: any) => {
        if (t.type && t.redirectUrls?.length) originalRedirectByType[t.type] = t.redirectUrls;
      });
      const currentByType = formData.redirectUrlsByType.value;
      const allCodes = [...new Set([...Object.keys(originalRedirectByType), ...Object.keys(currentByType)])];
      for (const code of allCodes) {
        const a = (originalRedirectByType[code] || []).join(',');
        const b = (currentByType[code] || []).join(',');
        if (a !== b) return true;
      }
    }
    
    return false;
  }, [product, formData]);

  // Handle API success states
  useEffect(() => {
    if (editProductState.value && !editProductState.loading && editProductState.loaded) {
      if (onSubmit) {
        const productData = {
          name: formData.name.value.trim(),
          description: formData.description.value.trim(),
          visibility: formData.visibility.value,
          approvalFlow: formData.approvalFlow.value
        };
        onSubmit(productData, false);
      }
      
      onClose();
    }
  }, [editProductState, formData, onSubmit, onClose]);
  
  // Form submission handler
  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isFormValid || !hasChanges) return;
    
    try {
      await updateExistingProduct(product?.name || formData.name.value);
    } catch (error) {
      console.error('Product update failed:', error);
    }
  }, [isFormValid, hasChanges, product, formData.name.value, updateExistingProduct]);

  // Make public confirmation: update form state and trigger product PUT with visibility public and approval open.
  const onMakePublicConfirm = useCallback(async () => {
    dispatch(updateFormField('visibility', 'public', true, null));
    dispatch(updateFormField('approvalFlow', 'open', true, null));
    await updateExistingProduct(product?.name || formData.name.value, {
      visibility: 'public',
      approvalFlow: 'open'
    });
  }, [dispatch, product, formData.name.value, updateExistingProduct]);

  return {
    handleSubmit,
    onMakePublicConfirm,
    isLoading,
    hasError,
    error,
    submitButtonText: 'Update',
    hasChanges
  };
}

export const ProductEditor: React.FC<ProductEditorProps> = ({ open, onClose, onSubmit, product }) => {
  const classes = useProductFormStyles();
  
  // Get form state from Redux
  const formData = useSelector((state: RootState) => state.productForm);
  const { isFormValid } = formData;
  
  // Use editor logic
  const { handleSubmit, onMakePublicConfirm, isLoading, hasError, error, submitButtonText, hasChanges } = useProductEditor({ open, onClose, onSubmit, product });

  const isProductPublicPersisted = (product?.visibility ?? '').toLowerCase() === 'public';
  const productDisplayName = product?.displayName ?? product?.name ?? '';
  
  return (
    <Drawer
      anchor="right"
      open={open}
      variant="temporary"
      onClose={onClose}
      classes={{
        paper: classes.drawer,
      }}
      ModalProps={{
        classes: {
          root: classes.modalRoot,
          backdrop: classes.backdrop
        },
        hideBackdrop: false,
      }}
      SlideProps={{
        mountOnEnter: true,
        unmountOnExit: true,
      }}
    >
      <Box className={classes.formContainer}>
        {/* Header with title and close button */}
        <Box className={classes.header}>
          <Typography className={classes.title}>
            <FormattedMessage
              defaultMessage="Edit {productName}"
              values={{ productName: productDisplayName || '—' }}
            />
          </Typography>
          <IconButton onClick={onClose} className={classes.closeButton} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {isProductPublicPersisted && <ProductPublicEditBanner />}
        
        <ProductForm
          handleSubmit={handleSubmit}
          hasError={hasError}
          error={error}
          onClose={onClose}
          isLoading={isLoading}
          submitButtonText={submitButtonText}
          isEditMode={true}
          onMakePublicConfirm={onMakePublicConfirm}
        />
         {/* Buttons */}
         <Box className={classes.buttonsContainer}>
          <Button
            onClick={onClose}
            className={classes.cancelButton}
            disabled={isLoading}
          >
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            className={classes.createButton}
            disabled={!isFormValid || !hasChanges || isLoading}
            onClick={handleSubmit}
            style={{ transition: 'all 0.3s ease-in-out' }}
          >
            {isLoading && (<CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }}/>)}
              <FormattedMessage defaultMessage={"Update"} />
            
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};